import { type ReactNode, useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';

const TOOLTIP_BASE =
  'pointer-events-none fixed z-50 w-64 rounded-lg bg-surface-elevated p-sm shadow-lg transition-all duration-150';
const TOOLTIP_W = 256;
const EDGE_PAD = 8;
const GAP = 4;

type Pos = { top: number; left: number };

function clampLeft(left: number): number {
  const max = window.innerWidth - TOOLTIP_W - EDGE_PAD;
  return Math.max(EDGE_PAD, Math.min(left, max));
}

function calcPos(rect: DOMRect): Pos {
  return { top: rect.bottom + GAP, left: clampLeft(rect.left) };
}

function Tooltip({
  text,
  show,
  pos,
}: {
  text: string;
  show: boolean;
  pos: Pos;
}): ReactNode {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <span
      className={cn(
        TOOLTIP_BASE,
        show ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
      )}
      style={{ top: pos.top, left: pos.left }}
    >
      <span className="block text-[11px] leading-relaxed text-text-secondary">
        {text}
      </span>
    </span>,
    document.body
  );
}

export function HelpTip({ text }: { text: string }): ReactNode {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<Pos>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (!show || !triggerRef.current) return;
    setPos(calcPos(triggerRef.current.getBoundingClientRect()));
  }, [show]);
  if (!text) return null;
  return (
    <span
      ref={triggerRef}
      className="relative -mx-2 inline-flex cursor-help items-center justify-center px-2 py-1 align-middle"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span className="text-xs font-bold leading-none text-brand-red">*</span>
      <Tooltip text={text} show={show} pos={pos} />
    </span>
  );
}
